import React, { useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import Modal from '../../components/Modal';
import { generateRecoverPasswordCode, updatePassword } from '../../services/api';
import './Login.css';
import './ResetPassword.css';
import { AuthArt, AuthFooter, AuthHeader } from './AuthLayout';
import { useMessages } from '../../i18n/I18n';
import { useFestivalId } from './useFestivalId';

const ResetPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [validationCode, setValidationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [repeatPassword, setRepeatPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  const location = useLocation();
  const history = useHistory();
  const t = useMessages('auth');

  const { festivalId, setFestivalId, isLocked, isResolving } = useFestivalId(location.search);

  React.useEffect(() => {
    const urlEmail = new URLSearchParams(location.search).get('email');
    if (urlEmail) setEmail(urlEmail);
  }, [location.search]);

  const handleGenerateCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      await generateRecoverPasswordCode(email, festivalId.trim());
      setIsModalOpen(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : t.sendCodeError);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== repeatPassword) {
      return;
    }

    setError(null);
    setIsLoading(true);

    try {
      await updatePassword(email, validationCode, newPassword, festivalId.trim());
      history.push(isLocked ? `/?festival_id=${festivalId}` : '/');
    } catch (err) {
      setError(err instanceof Error ? err.message : t.updatePasswordError);
      setIsModalOpen(false);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="auth-page reset-password-container">
      <main className="auth-panel">
        <AuthHeader />
        <form className="auth-form reset-password-form" onSubmit={handleGenerateCode}>
          <div className="auth-heading">
            <h2>{t.resetTitle}</h2>
            <p>{t.resetSubtitle}</p>
          </div>
          {isResolving ? (
            <p className="auth-resolving" role="status">
              <span className="auth-spinner" aria-hidden="true" />
              {t.findingFestival}
            </p>
          ) : !isLocked && (
            <label className="auth-field">
              <span>{t.festivalId}</span>
              <input
                type="text"
                placeholder={t.festivalId}
                value={festivalId}
                onChange={(e) => setFestivalId(e.target.value)}
                required
                disabled={isLoading}
              />
            </label>
          )}
          <label className="auth-field">
            <span>{t.email}</span>
            <input
              type="email"
              placeholder={t.email}
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={isLoading}
            />
          </label>
          {error && <p className="error-message" role="alert">{error}</p>}
          <button
            type="submit"
            disabled={isLoading || isResolving}
            className={`auth-submit${isLoading ? ' loading' : ''}`}
          >
            {isLoading ? t.sending : t.sendCode}
          </button>
        </form>
        <AuthFooter />

        <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
          <form className="password-update-form" onSubmit={handlePasswordUpdate}>
            <h3>{t.newPasswordTitle}</h3>
            <input
              type="text"
              placeholder={t.validationCode}
              value={validationCode}
              onChange={(e) => setValidationCode(e.target.value)}
              required
            />
            <input
              type="password"
              placeholder={t.newPassword}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
            />
            <input
              type="password"
              placeholder={t.repeatPassword}
              value={repeatPassword}
              onChange={(e) => setRepeatPassword(e.target.value)}
              required
            />
            <button
              type="submit"
              disabled={newPassword !== repeatPassword || isLoading}
            >
              {t.updatePassword}
            </button>
            {newPassword !== repeatPassword && (
              <p className="error-message">{t.passwordsDontMatch}</p>
            )}
          </form>
        </Modal>
      </main>
      <AuthArt />
    </div>
  );
};

export default ResetPassword; 